import { useCallback, useState, useMemo, useEffect, useRef } from 'react';

import { FormHandles } from '@unform/core';
import moment from 'moment';
import { IoFilterSharp } from 'react-icons/io5';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { InputSearch, Table, Button, ConfirmModal } from '~/components';
import Pagination from '~/components/Pagination';
import { useAuth } from '~/hooks/auth.context';
import { useLoading } from '~/hooks/loading';
import { IPagination, PageOptionsRequest } from '~/models/Common';
import { IUser } from '~/models/User';
import { IUserListFilters } from '~/models/UserListFilters';
import { ProfessionalService } from '~/services';
import { handleYupValidationError } from '~/utils';
import { applyMask } from '~/utils/masks';
import { UserListFiltersSchema } from '~/utils/schemas/UserListFiltersSchema';

import FilterForm from '../UsersList/FilterForm';

import * as S from './styles';
import { ActiveOrInactive } from './styles';

interface IUserDetailsProps {
  id: string;
}

interface IProfessionalDetailsState {
  professionalId: number;
}

const StudentsList = () => {
  const { setLoading } = useLoading();

  const router = useHistory();
  const formRef = useRef<FormHandles>(null);
  const { id } = useParams<IUserDetailsProps>();
  const { state } = useLocation<IProfessionalDetailsState>();
  const { authToken } = useAuth();
  const [students, setStudents] = useState<IPagination<IUser[]>>({
    data: [],
    lastPage: 0,
    page: 1,
    perPage: 1,
    total: '0',
  });
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filterText, setFilterText] = useState('Filtro avançado');
  const [filters, setFilters] = useState<IUserListFilters>({
    start_register_at: '',
    register_status: '',
    payment_status: '',
    search: '',
  });
  const [pageOptions, setPageOptions] = useState<PageOptionsRequest>({
    page: 1,
  });
  const [searchFilter, setSearchFilter] = useState('');

  const handleNavigateToUserDetails = useCallback(
    (user: IUser) => {
      router.push(`/users/details/student/${user.id}`, { user });
    },
    [router],
  );

  const handleNavigateToUserCadaster = useCallback(() => {
    router.push(`/students/cadaster`);
  }, [router]);

  const handleGetStudents = useCallback(async () => {
    if (!state.professionalId) return;
    try {
      setLoading(true);

      const { data } = await ProfessionalService.getProfessionalStudents(
        state.professionalId,
        pageOptions.page,
        9,
        filters?.search || undefined,
        filters?.register_status || undefined,
        filters?.payment_status || undefined,
        filters?.start_register_at || undefined,
      );
      setStudents(data);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Profissional com fatura não paga ou cancelada');
    }
  }, [pageOptions.page, setLoading, state.professionalId, filters]);

  useEffect(() => {
    handleGetStudents();
  }, [handleGetStudents]);

  const applyFilters = async (data: IUserListFilters) => {
    try {
      console.log('data:', data);
      formRef.current?.setErrors({});
      await UserListFiltersSchema.validate(data);
      setFilterText(validateFilterButton);
      setFilters({
        ...data,
        start_register_at: data.start_register_at
          ? moment(data.start_register_at, 'DD-MM-yyyy').format('yyyy-MM-DD')
          : '',
      });
      setFilterModalVisible((prevState) => !prevState);
    } catch (error) {
      console.log(error);
      handleYupValidationError(formRef, error);
    }
  };

  const StudentsTableMemo = useMemo(
    () => (
      <Table
        noItems={students.data?.length === 0}
        colsNames={[
          'Nome',
          'CPF',
          'E-mail',
          'Celular',
          'Status de acesso',
          // '',
        ]}
      >
        {students.data?.map((user) => (
          <tr key={user.id} onClick={() => handleNavigateToUserDetails(user)}>
            <td>{user.name}</td>
            <td>
              {user.student?.cpf &&
                applyMask({ name: 'cpf', value: user.student?.cpf })}
            </td>
            <td>{user.email}</td>
            <td>
              {user.cellphone &&
                applyMask({ name: 'telefone', value: user.cellphone })}
            </td>
            {/* <ActiveOrInactive isActive={user.student?.plan_status === 'active'}> */}
            <ActiveOrInactive isActive={user.student?.plan_status}>
              {user?.student?.plan_status ? 'Ativo' : 'Inativo'}
            </ActiveOrInactive>
            {/* <td>{'>'}</td> */}
          </tr>
        ))}
      </Table>
    ),
    [handleNavigateToUserDetails, students],
  );

  console.log('students:', students);

  const validateFilterButton = (): string => {
    const data = formRef.current?.getData() ?? {};
    let result = 'Filtro avançado';
    Object.values(data).forEach((value) => {
      if (value !== '') {
        result = 'Filtrando';
      }
    });
    return result;
  };

  console.log('filters:', filters);

  return (
    <>
      <S.Container>
        <S.UsersListHeader>
          <S.UserListTitle>Listagem de Alunos</S.UserListTitle>
          <InputSearch
            name="search"
            onSearch={(value) => setSearchFilter(value)}
            placeholder="Buscar por nome, e-mail ou CPF"
          />
          <Button
            onClick={() => setFilterModalVisible((prevState) => !prevState)}
            textContent={filterText}
            size="small"
            extraStyles={{ width: 180 }}
            variant={filterText !== 'Filtrando' ? 'border' : 'yellow'}
            icon={<IoFilterSharp size={20} />}
          />
          <Button
            onClick={() => {
              formRef.current?.reset({
                start_register_at: '',
                register_status: '',
                payment_status: '',
              });
              setFilters({});
              setFilterText('Filtro avançado');
            }}
            buttonDisable={filterText !== 'Filtrando'}
            textContent="Limpar filtros"
            size="small"
            variant="black"
          />
        </S.UsersListHeader>
        {StudentsTableMemo}
        <S.UsersListFooter>
          {/* <Button
            onClick={handleNavigateToUserCadaster}
            textContent="Cadastrar treinador"
            size="small"
            variant="black"
            extraStyles={{
              maxWidth: 300,
            }}
          /> */}
          <Pagination
            pageCount={students.lastPage!}
            pageRangeDisplayed={3}
            {...{ setPageOptions }}
          />
        </S.UsersListFooter>
      </S.Container>
      <ConfirmModal
        modalIsVisible={filterModalVisible}
        setModalIsVisible={() =>
          setFilterModalVisible((prevState) => !prevState)
        }
        handleSubmit={() => formRef.current?.submitForm()}
        title="Filtro avançado"
        confirmText="Aplicar filtros"
      >
        <FilterForm
          handleSubmit={applyFilters}
          formName="students-filters"
          formRef={formRef}
        />
      </ConfirmModal>
    </>
  );
};

export default StudentsList;
